import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  getDashboardErrorMessage,
  useGetDashboardArchivedSeriesQuery
} from './dashboardApi';
import { DASHBOARD_DATA_STATE } from './dashboardContract';
import { formatDashboardMetricValue } from './dashboardViewModel';

const AGGREGATION_LABELS = Object.freeze({
  average: 'Average',
  sum: 'Sum',
  latest: 'Latest value',
  minimum: 'Minimum',
  maximum: 'Maximum'
});

const formatDate = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format('MMM D, YYYY') : 'Date unavailable';
};

const formatValue = (
  metric,
  value,
  dataState = DASHBOARD_DATA_STATE.AVAILABLE
) => formatDashboardMetricValue(metric, { dataState, value });

const DashboardArchivedMetricHistory = ({ dateRange, locationId }) => {
  const historyQuery = useGetDashboardArchivedSeriesQuery(
    {
      locationId,
      fromDate: dateRange?.fromDate,
      toDate: dateRange?.toDate
    },
    { skip: !locationId || !dateRange }
  );
  const metrics = useMemo(
    () =>
      [...(historyQuery.data?.metrics ?? [])]
        .sort(
          (left, right) =>
            left.displayOrder - right.displayOrder ||
            left.label.localeCompare(right.label)
        )
        .map((metric) => ({
          ...metric,
          points: [...(metric.points ?? [])].sort((left, right) =>
            right.effectiveDate.localeCompare(left.effectiveDate)
          )
        })),
    [historyQuery.data?.metrics]
  );

  if (!dateRange) return null;

  return (
    <Paper
      data-testid="dashboard-archived-metric-history"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        mt: 4,
        p: { xs: 2, md: 3 }
      }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography component="h2" fontSize={18} fontWeight={700}>
            Archived metric history
          </Typography>
          <Typography color="text.secondary" variant="body2">
            View retained approved values for metrics that no longer accept new
            submissions.
          </Typography>
        </Box>
        <Chip label="Read only" size="small" variant="outlined" />
      </Stack>

      <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
        {formatDate(dateRange.fromDate)}–{formatDate(dateRange.toDate)}
      </Typography>

      {historyQuery.isFetching && <LinearProgress sx={{ mt: 2 }} />}
      {historyQuery.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(
            historyQuery.error,
            'Archived metric history could not be loaded.'
          )}
        </Alert>
      ) : !historyQuery.isLoading && metrics.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No archived metrics are available for this dashboard.
        </Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {metrics.map((metric) => (
            <Accordion
              disableGutters
              elevation={0}
              key={metric.metricDefinitionId}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ pr: 1, width: '100%' }}
                >
                  <Box>
                    <Typography fontWeight={700}>{metric.label}</Typography>
                    <Typography color="text.secondary" variant="body2">
                      {AGGREGATION_LABELS[metric.aggregation] ??
                        metric.aggregation}
                      :{' '}
                      {formatValue(
                        metric,
                        metric.aggregateValue,
                        metric.dataState
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      metric.archivedAt
                        ? `Archived ${formatDate(metric.archivedAt)}`
                        : 'Archive date unavailable'
                    }
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  color="text.secondary"
                  sx={{ mb: 1 }}
                  variant="body2"
                >
                  {metric.points.length}{' '}
                  {metric.points.length === 1
                    ? 'approved value'
                    : 'approved values'}{' '}
                  in this date range
                </Typography>
                {metric.points.length === 0 ? (
                  <Alert severity="info">
                    No approved values were found for this metric in the
                    selected date range.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table
                      aria-label={`${metric.label} archived values`}
                      size="small"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Approved value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metric.points.map((point) => (
                          <TableRow key={point.metricValueId}>
                            <TableCell>
                              {formatDate(point.effectiveDate)}
                            </TableCell>
                            <TableCell>
                              {formatValue(metric, point.value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default DashboardArchivedMetricHistory;
